import { differenceInCalendarDays, format, parseISO } from "date-fns";
import { FileSpreadsheet, Search } from "lucide-react";
import { useMemo } from "react";

import { PageLayout, PageTitle } from "components/layout";
import { Button } from "components/ui/button";
import {
  IStoreExchangeExportQueued,
  IStoreExchangeFilters,
  IStoreExchangeParams,
} from "src/@types/store-exchange";
import { toast } from "@/components/ui/use-toast";
import {
  useExportStoreExchangesExcelMutation,
  useGetStoreExchangeAllowedStoresQuery,
  useGetStoreExchangesQuery,
} from "@/services/store-exchange";
import { useGetStoresOptionsQuery } from "@/services/store/select-options";
import { layoutClasses } from "@/utils/css-classes";

import { StoreExchangeFilters } from "./components/store-exchange-filters";
import { StoreExchangePagination } from "./components/store-exchange-pagination";
import { StoreExchangeTable } from "./components/store-exchange-table";
import { useStoreExchangeFilterReducer } from "./reducers/useStoreExchangeFilterReducer";
import {
  DISPLAY_DATE_FORMAT,
  EMAIL_PLACEHOLDER,
  FilterOption,
  GLOBALLY_HANDLED_STATUS,
  ISO_DATE_LENGTH,
  MAX_PERIOD_DAYS,
  RETENTION_WARNING,
  STORE_EXCHANGE_LABELS,
  STORE_EXCHANGE_MESSAGES,
} from "./types";

const toNumber = (value: string): number | undefined => {
  const trimmed: string = value.trim();
  if (trimmed === "") return undefined;
  const parsed: number = Number(trimmed);
  return Number.isNaN(parsed) ? undefined : parsed;
};

const toText = (value: string): string | undefined => {
  const trimmed: string = value.trim();
  return trimmed === "" ? undefined : trimmed;
};

export default function StoreExchangeListingPage() {
  const { state, methods } = useStoreExchangeFilterReducer();
  const { draft, applied, page, perPage } = state;

  const { data: storeOptions } = useGetStoresOptionsQuery(undefined, {
    refetchOnMountOrArgChange: false,
  });
  const { data: allowedStores } = useGetStoreExchangeAllowedStoresQuery();

  const allowedCodes: number[] | null = allowedStores?.storeCodes ?? null;

  const availableStores = useMemo(() => {
    const all = (storeOptions ?? []) as FilterOption[];
    if (allowedCodes === null) return all;
    return all.filter((option: FilterOption) =>
      allowedCodes.includes(Number(option.value)),
    );
  }, [storeOptions, allowedCodes]);

  const isStoreLocked: boolean =
    allowedCodes !== null && availableStores.length === 1;

  const storeCode: number | undefined = isStoreLocked
    ? Number(availableStores[0].value)
    : draft.storeCode;

  const queryParams: IStoreExchangeParams | undefined = applied
    ? { ...applied, page, perPage }
    : undefined;

  const hasRequiredFilters: boolean =
    storeCode !== undefined &&
    draft.startDate !== undefined &&
    draft.endDate !== undefined;

  const { data: result, isFetching } = useGetStoreExchangesQuery(
    queryParams as IStoreExchangeParams,
    { skip: queryParams === undefined },
  );

  const [exportExcel, { isLoading: isExporting }] =
    useExportStoreExchangesExcelMutation();

  const buildFilters = (): IStoreExchangeFilters | undefined => {
    const { startDate, endDate, status } = draft;

    if (
      storeCode === undefined ||
      startDate === undefined ||
      endDate === undefined
    ) {
      toast(STORE_EXCHANGE_MESSAGES.MISSING_FILTERS);
      return undefined;
    }

    if (
      differenceInCalendarDays(parseISO(endDate), parseISO(startDate)) >
      MAX_PERIOD_DAYS
    ) {
      toast(STORE_EXCHANGE_MESSAGES.PERIOD_TOO_LONG);
      return undefined;
    }

    return {
      storeCode,
      startDate,
      endDate,
      status: status === undefined ? undefined : Number(status),
      controlNumber: toNumber(draft.controlNumber),
      customerDocument: toText(draft.customerDocument),
      operator: toText(draft.operator),
    };
  };

  const handleSearch = (): void => {
    const filters: IStoreExchangeFilters | undefined = buildFilters();
    if (filters === undefined) return;
    methods.applyFilters(filters);
  };

  const handleExport = async (): Promise<void> => {
    const filters: IStoreExchangeFilters | undefined = buildFilters();
    if (filters === undefined) return;

    try {
      const result = await exportExcel(filters).unwrap();
      const queued = result as IStoreExchangeExportQueued;

      if (queued?.jobId === undefined) {
        toast(STORE_EXCHANGE_MESSAGES.EMPTY_EXPORT);
        return;
      }

      toast({
        description: STORE_EXCHANGE_MESSAGES.EXPORT_QUEUED.description.replace(
          EMAIL_PLACEHOLDER,
          queued.email,
        ),
      });
    } catch (error) {
      const status = (error as { status?: number | string })?.status;
      if (!GLOBALLY_HANDLED_STATUS.has(status as number)) {
        toast(STORE_EXCHANGE_MESSAGES.EXPORT_ERROR);
      }
    }
  };

  const buildRetentionWarning = (): string | null => {
    const retentionStart: string | null = result?.retentionStart ?? null;
    if (retentionStart === null || applied === undefined) return null;
    if (applied.startDate >= retentionStart.slice(0, ISO_DATE_LENGTH)) {
      return null;
    }
    return RETENTION_WARNING.replace(
      "{date}",
      format(parseISO(retentionStart), DISPLAY_DATE_FORMAT),
    );
  };

  const retentionWarning: string | null = buildRetentionWarning();

  return (
    <PageLayout>
      <div className={layoutClasses.pageContainer}>
        <PageTitle
          title={STORE_EXCHANGE_LABELS.pageTitle}
          description={STORE_EXCHANGE_LABELS.pageDescription}
          titleSize="text-3xl"
          descriptionSize="text-xl"
        />

        <StoreExchangeFilters
          storeOptions={availableStores}
          storeCode={storeCode}
          isStoreLocked={isStoreLocked}
          onStoreChange={methods.setStore}
          startDate={draft.startDate}
          endDate={draft.endDate}
          onPeriodChange={methods.setPeriod}
          status={draft.status}
          controlNumber={draft.controlNumber}
          customerDocument={draft.customerDocument}
          operator={draft.operator}
          onStatusChange={methods.setStatus}
          onControlNumberChange={(value: string) =>
            methods.setTextField("controlNumber", value)
          }
          onCustomerDocumentChange={(value: string) =>
            methods.setTextField("customerDocument", value)
          }
          onOperatorChange={(value: string) =>
            methods.setTextField("operator", value)
          }
        />

        <div className="flex gap-3 mt-4">
          <Button
            onClick={handleSearch}
            disabled={!hasRequiredFilters || isFetching}
            className="h-8 bg-slate-800 hover:bg-slate-900 text-white"
          >
            <Search className="w-4 h-4 mr-2" />
            {isFetching
              ? STORE_EXCHANGE_LABELS.searching
              : STORE_EXCHANGE_LABELS.searchButton}
          </Button>
          <Button
            onClick={handleExport}
            disabled={!hasRequiredFilters || isExporting}
            className="h-8 bg-emerald-600 hover:bg-emerald-700 text-white"
          >
            <FileSpreadsheet className="w-4 h-4 mr-2" />
            {isExporting
              ? STORE_EXCHANGE_LABELS.generating
              : STORE_EXCHANGE_LABELS.excelButton}
          </Button>
        </div>

        {retentionWarning !== null && (
          <p className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded px-3 py-2 mt-4">
            {retentionWarning}
          </p>
        )}

        <StoreExchangeTable
          exchanges={result?.data ?? []}
          isLoading={isFetching}
          emptyMessage={
            applied === undefined
              ? STORE_EXCHANGE_LABELS.selectFiltersHint
              : STORE_EXCHANGE_LABELS.emptyResult
          }
        />

        <StoreExchangePagination
          page={page}
          pageSize={perPage}
          total={result?.total ?? 0}
          isFetching={isFetching}
          onPageChange={methods.setPage}
          onPageSizeChange={methods.setPageSize}
        />
      </div>
    </PageLayout>
  );
}
